import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Activity, 
  MapPin, 
  Clock, 
  TrendingUp,
  Footprints,
  User,
  Bike,
  Car,
  Train
} from 'lucide-react';
import axios from 'axios';

const Statistics = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get('/api/gps/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const transportModes = [
    { id: 'walking', name: 'Walking', icon: Footprints, color: '#ff4444' },
    { id: 'running', name: 'Running', icon: User, color: '#ff8800' },
    { id: 'biking', name: 'Biking', icon: Bike, color: '#44aa44' },
    { id: 'driving', name: 'Driving', icon: Car, color: '#4444ff' },
    { id: 'transit', name: 'Transit', icon: Train, color: '#8844ff' }
  ];

  const formatDistance = (distance) => {
    if (distance < 1) {
      return `${(distance * 1000).toFixed(0)}m`;
    }
    return `${distance.toFixed(1)}km`;
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center text-gray-500">
        <p>Failed to load statistics</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Statistics</h1>
        <p className="text-gray-600 mt-1">Your tracking activity and insights</p>
      </div>
      <div className="space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Sessions</p>
                <p className="text-xl font-semibold text-gray-900">
                  {stats.totalSessions}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Distance</p>
                <p className="text-xl font-semibold text-gray-900">
                  {formatDistance(stats.totalDistance)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Time</p>
                <p className="text-xl font-semibold text-gray-900">
                  {formatDuration(stats.totalDuration)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <MapPin className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed Blocks</p>
                <p className="text-xl font-semibold text-gray-900">
                  {stats.completedBlocksCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Transport Mode Breakdown */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Transport Mode Breakdown
          </h3>
          <div className="space-y-3">
            {transportModes.map((mode) => {
              const distance = stats.transportModes[mode.id] || 0;
              const percentage = stats.totalDistance > 0 
                ? (distance / stats.totalDistance * 100).toFixed(1)
                : 0;
              
              return (
                <div key={mode.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${mode.color}20` }}
                    >
                      <mode.icon 
                        className="w-4 h-4" 
                        style={{ color: mode.color }}
                      />
                    </div>
                    <span className="text-gray-700">{mode.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {formatDistance(distance)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {percentage}%
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Area Coverage */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Area Coverage
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Total Area Explored</span>
              <span className="text-sm font-medium text-gray-900">
                {stats.totalAreaKm2.toFixed(2)} km²
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Marked Areas</span>
              <span className="text-sm font-medium text-gray-900">
                {stats.markedAreasCount}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
